import React, { useState } from 'react';
import { useFutureOrderStore } from '../../../../components/Stores/FutureOrdersStore';
import { useAccountStore } from '../../../../components/Stores/accountStore';
import { AttachFileActionButton } from '../../../../components/UI-Elements/FileUpload/AttachFileScreen/AttachFileActionButton';
import { createFutureOrder, getAllFutureOrdersByAccount } from '../../../../sp-firebase/futureOrderFB';
import { LoadingLogoComponent } from '../../../Quotes/LoadingLogoComponent';
import { emptyFutureOrder, mockFutureOrder } from '../../futureOrderUtils';
import { PendingOverlayScreen } from '../../../../components/UI-Elements/FileUpload/PendingOverlay';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { comment, IFutureOrder } from '@sp-types/FutureOrderType';
import { Timestamp } from 'firebase/firestore';
import ButtonDesigned from '@components/UI-Elements/Buttons/ButtonDesigned';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { ConfirmDelete } from '../../../../components/UI-Elements/FileUpload/ErrorComponents/ConfirmDelete';
import { OPEN_STATUS_TYPES } from '../../../Dashboard/dashboard-components/DashboardNotifications/notificationUtils';

const FO_BUCKET_LOCATION = 'FutureOrders/';

interface Props {
  setNextScreen: () => void;
  setSummaryScreen: (_orderUUID: string) => void;
  isDemoMode: boolean;
  goBack: () => void;
}

export const FOAttachFiles: React.FC<Props> = ({ setNextScreen, isDemoMode, setSummaryScreen, goBack }) => {
  const [isSetPendingMode, setIsSetPendingMode] = useState(false);
  const { account } = useAccountStore();
  const { editedFutureOrder, setEditedFutureOrder } = useFutureOrderStore();
  const [isLoadingDoc, setIsLoadingDoc] = useState(false);
  const [uploadedFiles, setUploadedFiles] = React.useState<string[]>(editedFutureOrder.fileUrl || []);
  const [comments, setComments] = React.useState<comment[]>(editedFutureOrder.comments || []);
  const [exitPopup, setExitPopup] = React.useState<number>(-1);
  const [dragSectionText, setDragSectionText] = useState<[string, string, string]>([
    'Drag and Drop file',
    'File attached',
    'Pending...',
  ]);
  const navigate = useNavigate();
  const getCurrentFutureOrder = isDemoMode ? mockFutureOrder() : emptyFutureOrder;

  React.useEffect(() => {
    if (editedFutureOrder) {
      setUploadedFiles(editedFutureOrder.fileUrl);
    }
  }, [editedFutureOrder, editedFutureOrder.fileUrl]);

  async function onFileUploadedDemo(paths: string[]) {
    setIsLoadingDoc(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const updatedFutureOrder = {
      ...getCurrentFutureOrder,
      fileUrl: paths,
      futureOrderType: editedFutureOrder.futureOrderType,
      updatedAt: Timestamp.now(),
    };

    const orderUUID = await updateOrCreateOrder(updatedFutureOrder, account.id || '');
    setDragSectionText(['Drag and Drop file', 'File attached', 'Completed']);
    setEditedFutureOrder({ ...updatedFutureOrder, uuid: orderUUID });
    setSummaryScreen(orderUUID);
    setIsLoadingDoc(false);
    setUploadedFiles([]);
  }
  const fileNameRegex = /^(?:.*\/)([^/.*]+)_[0-9]*.*/;
  const loadDemoOrder = async (path: string): Promise<IFutureOrder | undefined> => {
    const allOrders = await getAllFutureOrdersByAccount(account?.id || '', OPEN_STATUS_TYPES);

    const matches = path.match(fileNameRegex);
    const orderId = matches?.[1];

    const demoOrder = allOrders?.find((a) => a.orderId === orderId);

    return demoOrder;
  };

  async function onFileUploaded(paths: string[]) {
    setIsLoadingDoc(true);

    if (account.id === 'Demo-Food') {
      const order = await loadDemoOrder(paths?.[0]);
      if (order?.orderId) {
        console.log('loading demo', order);
        const updatedFutureOrder = {
          ...order,
          fileUrl: paths,
          futureOrderType: editedFutureOrder.futureOrderType,
          updatedAt: Timestamp.now(),
        };

        //const orderUUID = await updateOrCreateOrder(updatedFutureOrder, account.id || '');
        setDragSectionText(['Drag and Drop file', 'File attached', 'Completed']);
        setEditedFutureOrder({ ...updatedFutureOrder });
        setIsLoadingDoc(false);
        setUploadedFiles([]);
        await new Promise((resolve) => setTimeout(resolve, 100));
        setSummaryScreen(order.uuid || '');
      }
      return;
    }
    if (account.id) {
      try {
        await createFutureOrder(account.id, {
          ...getCurrentFutureOrder,
          orderId: uuidv4().substring(0, 6),
          fileUrl: paths,
          futureOrderType: editedFutureOrder.futureOrderType,
          status: 'pending',
          comments,
          updatedAt: Timestamp.now(),
        });
        setDragSectionText(['Drag and Drop file', 'File attached', 'Completed']);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        setIsSetPendingMode(true);
        setIsLoadingDoc(false);
      } catch {
        toast.error('Save Failed - try again');
      }
    }
  }

  const handleCreateOrder = async () => {
    setIsLoadingDoc(true);
    setEditedFutureOrder({
      ...getCurrentFutureOrder,
      futureOrderType: editedFutureOrder.futureOrderType,
    });
    setNextScreen();
    setIsLoadingDoc(false);
  };

  const handleCreateDemoOrder = async () => {
    setIsLoadingDoc(true);
    const updatedFutureOrder = {
      ...getCurrentFutureOrder,
      futureOrderType: editedFutureOrder.futureOrderType,
      updatedAt: Timestamp.now(),
    };

    const orderUUID = await updateOrCreateOrder(updatedFutureOrder, account.id || '');
    setEditedFutureOrder({ ...updatedFutureOrder, uuid: orderUUID });
    setSummaryScreen(orderUUID);
    setIsLoadingDoc(false);
  };
  const fileSuffix = Date.now();
  const cancel = (isBack: boolean = false) => {
    if (uploadedFiles?.length) {
      setExitPopup(isBack ? 2 : 1);
      return;
    }
    if (isBack) {
      goBack();
      return;
    }
    cancelConfirm();
  };

  const cancelConfirm = () => {
    if (exitPopup === 2) {
      setExitPopup(-1);
      setUploadedFiles([]);

      goBack();
      return;
    }

    setExitPopup(-1);
    setUploadedFiles([]);

    navigate('/dashboard');
  };

  return (
    <>
      <div className=" flex-1 h-full flex flex-col justify-center items-center relative ">
        {isLoadingDoc && (
          <div className="absolute flex items-center justify-center inset-0">
            <LoadingLogoComponent size={70} />
          </div>
        )}
        <AttachFileActionButton
          dropZoneTitle={dragSectionText}
          bucketSaveLocation={FO_BUCKET_LOCATION + account.id + '/'}
          handleActionButton={isDemoMode ? handleCreateDemoOrder : handleCreateOrder}
          handleSubmitWithFilePath={(values: string[]) =>
            isDemoMode ? onFileUploadedDemo(values) : onFileUploaded(values)
          }
          fileNameSuffix={fileSuffix.toString()}
          onFilesChanged={(files: string[]) => setUploadedFiles(files)}
          onLoadChange={(isLoading: boolean) => setIsLoadingDoc(isLoading)}
          uploadedFiles={uploadedFiles}
          comments={comments}
          onCommentsChanged={(comms) => setComments(comms)}
        />
      </div>
      <div className="flex justify-between sticky bottom-0 bg-white ">
        <ButtonDesigned style="outline" onButtonClick={() => cancel(true)}>
          <ChevronLeftIcon />
          Back
        </ButtonDesigned>
        <div className="flex gap-3">
          <ButtonDesigned style="outline" onButtonClick={() => cancel(false)} className="p-2 w-28">
            Cancel
          </ButtonDesigned>
          <ButtonDesigned
            style="primary"
            disableButton={!uploadedFiles || uploadedFiles.length === 0}
            onButtonClick={() => onFileUploaded(uploadedFiles)}
            className="p-2 w-28"
          >
            Submit
          </ButtonDesigned>
        </div>
      </div>
      {isSetPendingMode && (
        <PendingOverlayScreen
          setDisplayOverlay={(value: boolean) => {
            setIsSetPendingMode(value);
            navigate('/dashboard');
          }}
        />
      )}
      {exitPopup !== -1 && (
        <ConfirmDelete
          onAction={cancelConfirm}
          onCancel={() => setExitPopup(-1)}
          description="Are you sure you want to delete attached files? This action is irreversible and will permanently remove the file from this order."
          title="Delete file"
          actionText="Delete"
        />
      )}
    </>
  );
};

const updateOrCreateOrder = async (futureOrder: IFutureOrder, accountID: string): Promise<string> => {
  try {
    const savedUuid = await createFutureOrder(accountID, {
      ...futureOrder,
      status: 'underwriting',
    });

    return savedUuid?.uuid || '';
  } catch (e) {
    console.error(e);
  }
  return '';
};
